import type { ReactElement } from 'react';
import { useEffect } from 'react';

import {
  fetchCultureSelectorData,
  useCultureSelectorData,
} from '@skyscanner-internal/global-components/culture-selector/helpers';
import FooterGC from '@skyscanner-internal/global-components/footer';
import { CultureSelectorButton } from '@web-platform/shared-banana-features-culture-selector';
import { setPrerenderReady } from '@web-platform/shared-banana-utils-frontend/src/helpers/setPrerenderReady';
import { PRERENDER_DEPS } from '@web-platform/shared-universal-constants/src/homepage/prerenderDependencies';
import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import { getFooterData } from '@web-platform/shared-universal-utils-frontend/src/singletons/footerData';
import { getCulture } from '@web-platform/shared-universal-utils-frontend-localisation';
import { logError, logWarn, logOperationalEvent } from 'saddlebag-logger';

const Footer = (): ReactElement | null => {
  const { locale } = getCulture();
  const includePreReleasedLocales = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.OPS.SERVE_PRE_RELEASE_LOCALES,
  );

  const {
    currencies,
    enMarkets,
    isFetchingCultureSelectorData,
    locales,
    markets,
    onCultureSelectorButtonClick,
  } = useCultureSelectorData(
    fetchCultureSelectorData,
    locale || 'en-GB',
    includePreReleasedLocales,
  );

  useEffect(() => {
    setPrerenderReady(PRERENDER_DEPS.FOOTER);
  }, []);

  const CultureSelectorButtonWithProps = (
    <CultureSelectorButton
      currencies={currencies}
      enMarkets={enMarkets}
      locales={locales}
      markets={markets}
      isFetching={isFetchingCultureSelectorData}
      onCultureSelectorButtonClick={onCultureSelectorButtonClick}
    />
  );

  const footerData = getFooterData();
  if (!footerData) return null;

  return (
    <FooterGC
      cultureData={footerData.cultureData}
      deviceInfo={footerData.deviceInfo}
      footerLinks={footerData.footerLinks}
      appDownLoadLinks={footerData.appDownLoadLinks}
      strings={footerData.strings}
      logger={{
        logOperationalEvent,
        logError,
        logWarn,
      }}
      isProductionEnv={process.env.NODE_ENV === 'production'}
      cultureSelectorButtonSlot={CultureSelectorButtonWithProps}
    />
  );
};

export default Footer;
